import { SelectItem } from '../types';
import { LoadOptions } from '../../types';
import { optionTypes } from 'ui/common/select/constants';

const haveNotFoundSelectedOption = (returnedOptions: SelectItem[], originOptions: SelectItem[]) => {
  return !returnedOptions?.length && !!originOptions?.length;
};

interface OptionsToHierarchyParams {
  originOptions: SelectItem[];
  loadOptions: LoadOptions;
  descriptionAllLevels: boolean;
  linkType?: optionTypes;
}

export const optionsToHierarchy = ({ 
  originOptions, loadOptions, descriptionAllLevels, linkType 
}: OptionsToHierarchyParams) => {
  
  const { parentId, siblingId } = loadOptions;
  const breadcrumbStack = [];
  let returnedOptions = [];
  let allLevelsDescriptionPrefix = '';
  
  const findOptionsAndBuildBreadcrumbStack = (addParentItemToStack: () => void, parentItem: SelectItem, items: SelectItem[]): boolean => {
    const buildStack = () => {
      if (addParentItemToStack !== undefined) {
        breadcrumbStack.unshift(parentItem);
        addParentItemToStack();
      }
    };

    for (let i = 0; i < items.length; i++) {
      const item = {...items[i], type: linkType};

      if (item.value === parentId) {
        breadcrumbStack.unshift(item);
        buildStack();
        returnedOptions = item.descendants;
        return true;
      }

      if (item.value === siblingId) {
        buildStack();
        returnedOptions = items;
        return true;
      }

      if (item?.descendants?.length > 0) {
        if (findOptionsAndBuildBreadcrumbStack(buildStack, item, item.descendants)) {
          return true;
        }
      }
    }
    return false;
  };

  if (originOptions && (siblingId !== undefined || parentId !== undefined)) {
    findOptionsAndBuildBreadcrumbStack(undefined, undefined, originOptions);
    if (haveNotFoundSelectedOption(returnedOptions, originOptions)) {
      returnedOptions = originOptions;
    }
  } else {
    returnedOptions = originOptions;
  }

  allLevelsDescriptionPrefix = !!descriptionAllLevels
    ? breadcrumbStack.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.description + ' - ';
    }, '')
    : '';

  breadcrumbStack.unshift({ value: 0, description: '', type: linkType } as SelectItem);

  return {
    allLevelsDescriptionPrefix,
    breadcrumbStack,
    returnedOptions
  };
};
export const optionsToHierarchyFilterReplacements = ({
  originOptions, loadOptions, descriptionAllLevels, linkType
}: OptionsToHierarchyParams) => {

  const { optionDomainId, parentId, siblingId } = loadOptions;
  let breadcrumbStack: SelectItem[] = [];
  let returnedOptions = [];
  let allLevelsDescriptionPrefix = '';

  const isMatchingByValueAndoptionDomainId = (item: SelectItem, targetValue: number) => {
    const hasTargetValue = item.value === targetValue;
    if (!hasTargetValue) {
      return false;
    }

    if (optionDomainId === undefined) {
      return true;
    }

    return item.optionDomainId === optionDomainId;
  };

  const findOptionsAndBuildPath = (items: SelectItem[], currentPath: SelectItem[]): boolean => {
    return items.some(originalItem => {
      const item = { ...originalItem, type: linkType };
      const newPath = [...currentPath, item];

      const isTargetByParent = parentId !== undefined && isMatchingByValueAndoptionDomainId(item, parentId);
      const isTargetBySibling = siblingId !== undefined && isMatchingByValueAndoptionDomainId(item, siblingId);

      if (isTargetByParent) {
        breadcrumbStack = newPath;
        returnedOptions = item.descendants || [];
        return true;
      }

      if (isTargetBySibling) {
        breadcrumbStack = currentPath;
        returnedOptions = items;
        return true;
      }

      return item?.descendants?.length > 0
        ? findOptionsAndBuildPath(item.descendants, newPath)
        : false;
    });
  };

  if (originOptions && (siblingId !== undefined || parentId !== undefined)) {
    const found = findOptionsAndBuildPath(originOptions, []);
    if (!found || haveNotFoundSelectedOption(returnedOptions, originOptions)) {
      returnedOptions = originOptions;
      breadcrumbStack = [];
    }
  } else {
    returnedOptions = originOptions;
  }

  allLevelsDescriptionPrefix = !!descriptionAllLevels
    ? breadcrumbStack.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.description + ' - ';
    }, '')
    : '';

  breadcrumbStack.unshift({ value: 0, description: '', type: linkType } as SelectItem);

  return {
    allLevelsDescriptionPrefix,
    breadcrumbStack,
    returnedOptions
  };
};

export const optionsToHierarchyFactory = ({
  originOptions, loadOptions, descriptionAllLevels, linkType
}: OptionsToHierarchyParams) => {

  if (!!loadOptions.optionDomainId) {
    return optionsToHierarchyFilterReplacements({ originOptions, loadOptions, descriptionAllLevels, linkType });
  }
  return optionsToHierarchy({ originOptions, loadOptions, descriptionAllLevels, linkType });
};
